import { memo } from "react";
import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ProgressBar } from "@/components/shared/progress-bar";
import { getFiscalLabel } from "@/lib/fiscal-year";
import type { KrType } from "@/types/entities";

export interface KrNodeData extends Record<string, unknown> {
  krId: string;
  title: string;
  status: string | null;
  completionPercent: number | null;
  quarter: string | null;
  dueDate: string | null;
  krType: KrType | null;
  targetValue: number | null;
  currentValue: number | null;
  year: number | null;
  onShowDetail: ((id: string) => void) | null;
}

export type KrNodeFlowType = Node<KrNodeData, "kr">;

const KR_TYPE_LABELS: Record<KrType, string> = {
  percentage: "%",
  binary: "Y/N",
  metric: "#",
};

function KrNodeComponent({ data }: NodeProps<KrNodeFlowType>) {
  const pct = data.completionPercent ?? 0;

  return (
    <div
      className={cn(
        "w-50 rounded-lg border bg-[hsl(var(--card))] shadow-sm border-l-4 border-l-[hsl(var(--muted-foreground))]",
        data.onShowDetail && "cursor-pointer",
      )}
      onClick={() => data.onShowDetail?.(data.krId)}
    >
      <Handle type="target" position={Position.Top} className="!bg-[hsl(var(--primary))]" />

      {/* Header with KR badge and quarter */}
      <div className="flex items-center justify-between border-b px-2.5 py-1">
        <div className="flex items-center gap-1">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            KR
          </Badge>
          {data.krType && (
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
              {KR_TYPE_LABELS[data.krType]}
            </span>
          )}
        </div>
        {data.quarter && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {data.quarter}{data.year ? (() => { const fy = getFiscalLabel(data.quarter, data.year); return fy ? ` ${fy}` : ""; })() : ""}
          </Badge>
        )}
      </div>

      {/* Body */}
      <div className="px-2.5 py-1.5">
        <p className="text-xs font-medium leading-tight line-clamp-2">{data.title}</p>
        {data.dueDate && (
          <p className="mt-0.5 text-[10px] text-[hsl(var(--muted-foreground))]">
            Due {data.dueDate}
          </p>
        )}
      </div>

      {/* Footer with progress */}
      <div className="border-t px-2.5 py-1.5">
        <div className="flex items-center gap-1.5">
          <ProgressBar value={pct} className="flex-1" />
          <span className="shrink-0 text-[10px] font-medium">
            {Math.round(pct)}%
          </span>
        </div>
      </div>
    </div>
  );
}

export const KrNode = memo(KrNodeComponent);
